import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  Container,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { HeaderBreadcrumbs, Page, Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableEmptyRows, TableHeadCustom, TableNoData } from '@/components/table';
import useTable, { emptyRows, getComparator } from '@/hooks/useTable';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import { PATH_USER } from '@/features/app';
import TransactionsTableRow from '../components/TransactionsTableRow';
import TransactionsTableToolbar from '../components/TransactionsTableToolbar';
import { getTransactions } from '../api';
import {
  TRANSACTIONS_TABLE_HEAD,
  buildTransactionSourceOptions,
  buildTransactionStatusOptions,
  buildTransactionTypeOptions,
  isFiltersApplied,
} from '../utils';
import { PaymentTransaction, TransactionFilters, TransactionOption } from '../types';

type TransactionsListFilters = TransactionFilters;

const defaultFilters: TransactionsListFilters = {
  query: '',
  status: '',
  type: '',
  source: '',
};

export default function TransactionsList() {
  const table = useTable({
    defaultOrderBy: 'created_at',
    defaultOrder: 'desc',
  });
  const { themeStretch } = useSettings();
  const { errorMsg } = useSnackbarMsg();
  const [filters, setFilters] = useState<TransactionsListFilters>(defaultFilters);

  const { data, isLoading } = useQuery({
    queryKey: [
      'transactions',
      table.page,
      table.rowsPerPage,
      table.order,
      table.orderBy,
      filters.status,
      filters.type,
      filters.source,
      filters.query,
    ],
    queryFn: () =>
      getTransactions({
        page: table.page + 1,
        limit: table.rowsPerPage,
        status: filters.status !== '' ? String(filters.status) : undefined,
        type: filters.type !== '' ? String(filters.type) : undefined,
        source: filters.source || undefined,
        search: filters.query || undefined,
        sortBy: table.orderBy,
        sortOrder: table.order,
      }),
    onError: (error) => {
      errorMsg(error, 'Unable to load transactions');
    },
  });

  const transactions = data?.data ?? [];
  const meta = data?.meta ?? { page: 1, limit: 25, total: 0, totalPages: 1 };

  const statusOptions = useMemo<TransactionOption[]>(() => {
    return buildTransactionStatusOptions(transactions);
  }, [transactions]);

  const typeOptions = useMemo<TransactionOption[]>(() => {
    return buildTransactionTypeOptions(transactions);
  }, [transactions]);

  const sourceOptions = useMemo<TransactionOption[]>(() => {
    return buildTransactionSourceOptions(transactions);
  }, [transactions]);

  const handleFilters = (name: keyof TransactionsListFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    table.setPage(0);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    table.setPage(0);
  };

  const dataFiltered = transactions;

  const denseHeight = table.dense ? 52 : 72;
  const isFiltered = isFiltersApplied(filters);
  const isNotFound = !dataFiltered.length;

  const dataInPage = dataFiltered;

  return (
    <Page title="Transactions">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Transactions"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Transactions' },
          ]}
        />

        <Card>
          <TransactionsTableToolbar
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            statusOptions={statusOptions}
            typeOptions={typeOptions}
            sourceOptions={sourceOptions}
            isFiltered={isFiltered}
          />

          {isLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1000 }}>
                  <Table size={table.dense ? 'small' : 'medium'}>
                    <TableHeadCustom
                      order={table.order}
                      orderBy={table.orderBy}
                      headLabel={TRANSACTIONS_TABLE_HEAD}
                      rowCount={dataFiltered.length}
                      numSelected={table.selected.length}
                      onSort={table.onSort}
                    />
                    <TableBody>
                      {dataInPage.map((row: PaymentTransaction) => (
                        <TransactionsTableRow key={row.id} row={row} />
                      ))}

                      {dataInPage.length < table.rowsPerPage && (
                        <TableEmptyRows
                          height={denseHeight}
                          emptyRows={table.rowsPerPage - dataInPage.length}
                        />
                      )}

                      <TableNoData
                        isNotFound={isNotFound}
                        title={
                          isNotFound
                            ? isFiltered
                              ? 'No transactions found'
                              : 'No transactions available'
                            : undefined
                        }
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                {(meta?.total ?? 0) > 25 && (
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={meta?.total ?? 0}
                    rowsPerPage={table.rowsPerPage}
                    page={table.page}
                    onPageChange={table.onChangePage}
                    onRowsPerPageChange={table.onChangeRowsPerPage}
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch checked={table.dense} onChange={table.onChangeDense} />
                  }
                  label="Dense"
                  sx={{
                    px: 3,
                    py: 1.5,
                    top: 0,
                    position: { md: 'absolute' },
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>
    </Page>
  );
}
